// OfferPageBuilderSimple.js — Orchestrator
// Wires all extracted hooks + panels together via OfferBuilderContext.
// Original file was 20,094 lines; logic lives in OfferPageBuilder/ directory.
import React, { useCallback, useEffect, useMemo, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Tabs,
  Tab,
  Divider,
  Chip,
  useTheme,
  useMediaQuery,
  SwipeableDrawer,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  Switch,
  Alert,
  AlertTitle,
  Slider,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Title as TitleIcon,
  TextFields as TextIcon,
  Image as ImageIcon,
  SmartButton as ButtonIcon,
  Code as CodeIcon,
  VideoLibrary as VideoIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as PreviewIcon,
  GetApp as ExportIcon,
  Link as LinkIcon,
  Settings as SettingsIcon,
  ArrowUpward as MoveUpIcon,
  KeyboardArrowDown as MoveDownIcon,
  Save as SaveIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  Phone as PhoneIcon,
  Tablet as TabletIcon,
  Computer as ComputerIcon,
  Verified as VerifiedIcon,
  Lock as LockIcon,
  ViewModule as TemplateIcon,
  ViewColumn as ColumnIcon,
  CloudUpload as UploadIcon,
  ShoppingCart as ShoppingCartIcon,
  ArrowForward as ArrowForwardIcon,
  Star as StarIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Timeline as TimelineIcon,
  ExpandMore as ExpandMoreIcon,
  DragIndicator as DragIcon,
  FileCopy as DuplicateIcon,
  Cancel as CancelIcon,
  CallSplit as CallSplitIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  FormatListBulleted as ListIcon,
  Check as CheckMarkIcon,
  Clear as CloseMarkIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { getPreset, getPresetFontFamilies } from '../textStyle.js';
import { API_URL } from '../config';
import { getPreBuiltTemplates, getPreBuiltTemplatesByType, getAllTemplatesByType } from '../services/offerTemplateService';
import brandService from '../services/brandService';
import { getOffersByBrand } from '../services/offerService';
import { uploadToS3WithProgress } from '../services/s3UploadService';
import api from '../services/api';
import { getListIcon, getListIconHTML } from '../utils/offerListIcons';
import { ELEMENT_TYPES, LAYOUT_TYPES, TEMPLATE_TYPE_OPTIONS } from '../utils/offerElementTypes';
import { DEFAULT_ELEMENTS } from '../utils/offerDefaultElements';
import { LAYOUT_CONFIGS } from '../utils/offerLayoutConfigs';
import {
  COLOR_PRESETS,
  BRANDS_COLOR_PRESETS,
  TEXT_COLOR_PRESETS,
  BRANDS_TEXT_COLOR_PRESETS,
} from '../utils/offerColorPresets';
import {
  BRANDS_REF_DEVICE_WIDTH,
  BRANDS_REF_DEVICE_HEIGHT,
  BRANDS_EDITOR_DEVICE_WIDTH,
  BRANDS_EDITOR_DEVICE_HEIGHT,
  BRANDS_EDITOR_LABEL,
  BRANDS_PAGE_MAX_HEIGHT,
  BRANDS_EDITOR_PREVIEW_COL_WIDTH,
  BRANDS_EDITOR_RIGHT_PANEL_WIDTH,
  BRANDS_EDITOR_SAVED_COL_WIDTH,
  SETUPS_REF_DEVICE_WIDTH,
  SETUPS_REF_DEVICE_HEIGHT,
  SETUPS_EDITOR_DEVICE_WIDTH,
  SETUPS_EDITOR_DEVICE_HEIGHT,
  SETUPS_EDITOR_LABEL,
  SETUPS_EDITOR_PREVIEW_COL_WIDTH,
} from '../utils/offerDeviceConstants';
import { getSuggestedTextColors } from '../utils/offerColorUtils';
import { renderPreviewElement, calculateVideoAspectRatio } from '../utils/offerPreviewElement';

// Extracted hooks
import { useOfferBuilderState } from './OfferPageBuilder/hooks/useOfferBuilderState';
import { useZoneHeight } from './OfferPageBuilder/hooks/useZoneHeight';
import { usePagination } from './OfferPageBuilder/hooks/usePagination';
import { useElementOperations } from './OfferPageBuilder/hooks/useElementOperations';
import { useTemplates } from './OfferPageBuilder/hooks/useTemplates';
import { useSavedElements } from './OfferPageBuilder/hooks/useSavedElements';
import { useBrands } from './OfferPageBuilder/hooks/useBrands';

// Extracted utils
import { getInlineBackgroundColors, getInlineTextColors, getCurrentFontFamily } from './OfferPageBuilder/utils/colorHelpers';
import { applyHighlight, removeAllHighlights } from './OfferPageBuilder/utils/textEditorUtils';
import { generateElementHTML } from './OfferPageBuilder/utils/generateElementHTML';

// Extracted panels
import { useElementRenderer } from './OfferPageBuilder/panels/ElementRenderer';
import LeftPanel from './OfferPageBuilder/panels/LeftPanel';
import PreviewCanvas from './OfferPageBuilder/panels/PreviewCanvas';
import RightPanel from './OfferPageBuilder/panels/RightPanel';
import MobileToolbar from './OfferPageBuilder/panels/MobileToolbar';

// Context
import { OfferBuilderContext } from './OfferPageBuilder/OfferBuilderContext';

const OfferPageBuilderSimple = forwardRef(({
  templatesOnly = false,
  customElementsOnly = false,
  hideCustomElements = false,
  hideLoadTemplate = false,
  templateSelectionOnly = false,
  typeAndSubtypeOnly = false,
  templatesOnlyMode = false,
  isBrandsPage = false,
  sharedElements = null, setSharedElements = null,
  sharedPages = null, setSharedPages = null,
  sharedSelectedElement = null, setSharedSelectedElement = null,
  sharedSelectedElementContext = null, setSharedSelectedElementContext = null,
  sharedSelectedCategory = null, setSharedSelectedCategory = null,
  sharedSelectedSubtype = null, setSharedSelectedSubtype = null,
  sharedSelectedBrand = null, setSharedSelectedBrand = null,
  sharedSelectedOffer = null, setSharedSelectedOffer = null,
  sharedSelectedTemplateId = null, setSharedSelectedTemplateId = null,
}, ref) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // ── All state ─────────────────────────────────────────────────────────────
  const {
    localElements, setLocalElements,
    localSelectedElement, setLocalSelectedElement,
    localSelectedElementContext, setLocalSelectedElementContext,
    localPages, setLocalPages,
    pages, setPages,
    currentPageIndex, setCurrentPageIndex,
    nextPageId, setNextPageId,
    paginationEnabled, setPaginationEnabled,
    isLoadingExistingContent, setIsLoadingExistingContent,
    insertAfterElementId, setInsertAfterElementId,
    localRatingText, setLocalRatingText,
    getCurrentPage, currentPageElements,
    elements, setElements,
    selectedElement, setSelectedElement,
    selectedElementContext, setSelectedElementContext,
    isEditingMode,
    selectedSubElement, setSelectedSubElement,
    drawerOpen, setDrawerOpen,
    previewPanelOpen, setPreviewPanelOpen,
    settingsOpen, setSettingsOpen,
    settingsAnchorEl, setSettingsAnchorEl,
    pageSettingsOpen, setPageSettingsOpen,
    tabValue, setTabValue,
    popoverPosition, setPopoverPosition,
    isDragging, setIsDragging,
    dragOffset, setDragOffset,
    showLayoutSelector, setShowLayoutSelector,
    targetZone, setTargetZone,
    draggedElement, setDraggedElement,
    dragOverZone, setDragOverZone,
    editingElement, setEditingElement,
    editingField, setEditingField,
    tempValue, setTempValue,
    deletedElementsHistory, setDeletedElementsHistory,
    showUndoNotification, setShowUndoNotification,
    contentTabTempValue, setContentTabTempValue,
    buttonContentTabTempValue, setButtonContentTabTempValue,
    buttonUrlTabTempValue, setButtonUrlTabTempValue,
    listItemTextValues, setListItemTextValues,
    savedTemplates, setSavedTemplates,
    preBuiltTemplates, setPreBuiltTemplates,
    buttonImageCalloutTemplates, setButtonImageCalloutTemplates,
    buttonNoticeTemplates, setButtonNoticeTemplates,
    buttonTextTemplates, setButtonTextTemplates,
    templateName, setTemplateName,
    templateType, setTemplateType,
    templateSubtype, setTemplateSubtype,
    showSaveDialog, setShowSaveDialog,
    showTemplatesList, setShowTemplatesList,
    currentTemplateId, setCurrentTemplateId,
    pageSettings, setPageSettings,
    mobileDrawerOpen, setMobileDrawerOpen,
    rightPanelOpen, setRightPanelOpen,
    previewDevice, setPreviewDevice,
    forceRender, setForceRender,
    textEditorRef,
    selectedCustomCategory, setSelectedCustomCategory,
    selectedCustomSubtype, setSelectedCustomSubtype,
    filteredTemplates, setFilteredTemplates,
    selectedVariantKey, setSelectedVariantKey,
    selectedTemplateId, setSelectedTemplateId,
    iconSelectorOpen, setIconSelectorOpen,
    imageUploading, setImageUploading,
    uploadingElementId, setUploadingElementId,
    generatingLink, setGeneratingLink,
    savedOfferElements, setSavedOfferElements,
    filteredSavedElements, setFilteredSavedElements,
    currentElementName, setCurrentElementName,
    showSaveElementDialog, setShowSaveElementDialog,
    showBrandOfferAlert, setShowBrandOfferAlert,
    showLoadTemplateAlert, setShowLoadTemplateAlert,
    loadTemplateError, setLoadTemplateError,
    showStatusAlert, setShowStatusAlert,
    statusMessage, setStatusMessage,
    statusTitle, setStatusTitle,
    showReplaceConfirm, setShowReplaceConfirm,
    showConfirmDialog, setShowConfirmDialog,
    confirmDialogTitle, setConfirmDialogTitle,
    confirmDialogMessage, setConfirmDialogMessage,
    confirmAction, setConfirmAction,
    showPromptDialog, setShowPromptDialog,
    promptDialogTitle, setPromptDialogTitle,
    promptDialogMessage, setPromptDialogMessage,
    promptDialogValue, setPromptDialogValue,
    promptAction, setPromptAction,
    pendingTemplateData, setPendingTemplateData,
    showRenameDialog, setShowRenameDialog,
    renameElementId, setRenameElementId,
    renameElementValue, setRenameElementValue,
    isUpdatingElement, setIsUpdatingElement,
    selectedSavedElementId, setSelectedSavedElementId,
    saveType, setSaveType,
    selectedBrand, setSelectedBrand,
    selectedOffer, setSelectedOffer,
    brands, setBrands,
    offers, setOffers,
    zoneHeightModes, setZoneHeightModes,
    heightOverflowAlerts, setHeightOverflowAlerts,
    loadingBrands, setLoadingBrands,
    loadingOffers, setLoadingOffers,
    isResizing, setIsResizing,
    resizeDirection, setResizeDirection,
    resizeStartData, setResizeStartData,
    isResizingRef,
    resizeStartDataRef,
    resizeDirectionRef,
  } = useOfferBuilderState({
    isBrandsPage,
    templatesOnly,
    sharedElements,
    setSharedElements,
    sharedPages,
    setSharedPages,
    sharedSelectedElement,
    setSharedSelectedElement,
    sharedSelectedElementContext,
    setSharedSelectedElementContext,
    sharedSelectedCategory,
    setSharedSelectedCategory,
    sharedSelectedSubtype,
    setSharedSelectedSubtype,
    sharedSelectedBrand,
    setSharedSelectedBrand,
    sharedSelectedOffer,
    setSharedSelectedOffer,
    sharedSelectedTemplateId,
    setSharedSelectedTemplateId,
  });

  const generateId = () => `element_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  // ── Zone Height ───────────────────────────────────────────────────────────
  const {
    getZoneHeight,
    getZoneHeightStyle,
    checkZoneHeightOverflow,
    clearOverflowAlert,
    getOverflowMessage,
    getCanvasOverflowAlert,
    toggleZoneHeight,
    getExpandIcon,
    getHeightTooltip,
    getHeightModeText,
    STANDARD_MOBILE,
  } = useZoneHeight({
    zoneHeightModes,
    setZoneHeightModes,
    heightOverflowAlerts,
    setHeightOverflowAlerts,
    elements,
  });

  // ── Element Operations ───────────────────────────────────────────────────
  const {
    findAndUpdateLayout,
    addLayout,
    addElementToZone,
    deleteElementFromZone,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    onDragEnd,
    startInlineEdit,
    saveInlineEdit,
    cancelInlineEdit,
    handleKeyPress,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleResizeMove,
    handleResizeEnd,
    handleResizeStart,
    deleteElement,
    undoDelete,
    dismissUndoNotification,
    updateElement,
    saveContentTabEdit,
    cancelContentTabEdit,
    handleContentTabKeyPress,
    saveButtonContentTabEdit,
    cancelButtonContentTabEdit,
    handleButtonContentTabKeyPress,
    saveButtonUrlTabEdit,
    cancelButtonUrlTabEdit,
    handleButtonUrlTabKeyPress,
    moveElement,
  } = useElementOperations({
    elements,
    setElements,
    pages,
    setPages,
    currentPageIndex,
    paginationEnabled,
    selectedElement,
    setSelectedElement,
    selectedElementContext,
    setSelectedElementContext,
    editingElement,
    setEditingElement,
    editingField,
    setEditingField,
    tempValue,
    setTempValue,
    contentTabTempValue,
    setContentTabTempValue,
    buttonContentTabTempValue,
    setButtonContentTabTempValue,
    buttonUrlTabTempValue,
    setButtonUrlTabTempValue,
    deletedElementsHistory,
    setDeletedElementsHistory,
    showUndoNotification,
    setShowUndoNotification,
    rightPanelOpen,
    setRightPanelOpen,
    tabValue,
    setTabValue,
    isDragging,
    setIsDragging,
    dragOffset,
    setDragOffset,
    draggedElement,
    setDraggedElement,
    dragOverZone,
    setDragOverZone,
    isResizing,
    setIsResizing,
    resizeDirection,
    setResizeDirection,
    resizeStartData,
    setResizeStartData,
    isResizingRef,
    resizeStartDataRef,
    resizeDirectionRef,
    forceRender,
    setForceRender,
    // previously missing
    isBrandsPage,
    popoverPosition,
    setPopoverPosition,
    showLayoutSelector,
    setShowLayoutSelector,
    targetZone,
    setTargetZone,
  });

  // ── Brands / Offers / Uploads ─────────────────────────────────────────────
  const {
    populateOfferLinkInElements,
    loadBrands,
    loadOffersForBrand,
    handleBrandChange,
    handleVideoUpload,
    handleThumbnailUpload,
    handleImageUpload,
    handleMediaUploadForElement,
  } = useBrands({
    isBrandsPage,
    selectedBrand,
    setSelectedBrand,
    selectedOffer,
    setSelectedOffer,
    brands,
    setBrands,
    offers,
    setOffers,
    elements,
    setElements,
    pages,
    setPages,
    paginationEnabled,
    currentPageIndex,
    setImageUploading,
    setUploadingElementId,
    setStatusTitle,
    setStatusMessage,
    setShowStatusAlert,
    selectedElementContext,
    setForceRender,
    findAndUpdateLayout,
    updateElement,
    templateSelectionOnly,
    // previously missing
    imageUploading,
    selectedElement,
    setSelectedElement,
    setButtonContentTabTempValue,
    setButtonUrlTabTempValue,
    setFilteredSavedElements,
    setSavedOfferElements,
    templatesOnly,
    setLoadingBrands: () => {},
    setLoadingOffers: () => {},
  });

  // ── Pagination ───────────────────────────────────────────────────────────
  const {
    calculateElementHeight,
    calculatePageHeight,
    checkBrandsPageOverflow,
    getBrandsPageOverflowInfo,
    getMaxPageHeight,
    checkPageOverflow,
    checkAndEnablePagination,
    togglePagination,
    splitPageAtElement,
    mergeWithNextPage,
    moveToNewPage,
    addPage,
    goToPage,
    goToNextPage,
    goToPreviousPage,
    deletePage,
    addElement,
    MOBILE_PHONE_HEIGHT,
  } = usePagination({
    pages,
    setPages,
    currentPageIndex,
    setCurrentPageIndex,
    nextPageId,
    setNextPageId,
    paginationEnabled,
    setPaginationEnabled,
    isEditingMode,
    elements,
    setElements,
    localElements,
    isBrandsPage,
    sharedElements,
    selectedElement,
    setSelectedElement,
    setTabValue,
    setRightPanelOpen,
    insertAfterElementId,
    setInsertAfterElementId,
    setShowLayoutSelector,
    isLoadingExistingContent,
    setIsLoadingExistingContent,
    selectedBrand,
    selectedOffer,
  });

  // ── Templates ─────────────────────────────────────────────────────────────
  const {
    loadTemplates,
    saveTemplate,
    loadTemplate,
    deleteTemplate,
    createNewTemplate,
    loadPreBuiltTemplate,
    loadPreBuiltTemplates,
    loadAllTemplatesForPopup,
    loadTemplatesByTypeAndSubtype,
    loadTemplatesByType,
    forceTemplateStateReset,
    filterLegitimateTemplates,
    groupTemplatesByType,
  } = useTemplates({
    isBrandsPage,
    templatesOnly,
    elements,
    setElements,
    pages,
    setPages,
    pageSettings,
    setPageSettings,
    currentPageIndex,
    setCurrentPageIndex,
    nextPageId,
    setNextPageId,
    paginationEnabled,
    setPaginationEnabled,
    localElements,
    setLocalElements,
    savedTemplates,
    setSavedTemplates,
    preBuiltTemplates,
    setPreBuiltTemplates,
    buttonImageCalloutTemplates,
    setButtonImageCalloutTemplates,
    buttonNoticeTemplates,
    setButtonNoticeTemplates,
    buttonTextTemplates,
    setButtonTextTemplates,
    templateName,
    setTemplateName,
    templateType,
    setTemplateType,
    templateSubtype,
    setTemplateSubtype,
    showSaveDialog,
    setShowSaveDialog,
    showTemplatesList,
    setShowTemplatesList,
    currentTemplateId,
    setCurrentTemplateId,
    filteredTemplates,
    setFilteredTemplates,
    selectedCustomCategory,
    setSelectedCustomCategory,
    selectedCustomSubtype,
    setSelectedCustomSubtype,
    selectedVariantKey,
    setSelectedVariantKey,
    selectedTemplateId,
    setSelectedTemplateId,
    setShowReplaceConfirm,
    setPendingTemplateData,
    setShowLoadTemplateAlert,
    setLoadTemplateError,
    setStatusTitle,
    setStatusMessage,
    setShowStatusAlert,
    populateOfferLinkInElements,
    // previously missing
    customElementsOnly,
    offers,
    selectedOffer,
    setConfirmAction,
    setConfirmDialogMessage,
    setConfirmDialogTitle,
    setCurrentElementName,
    setForceRender,
    setIsUpdatingElement,
    setSelectedElement,
    setSelectedSavedElementId,
    setSharedElements,
    setSharedPages,
    setShowConfirmDialog,
    sharedElements,
    sharedPages,
  });

  // ── Saved Elements ────────────────────────────────────────────────────────
  const {
    saveCurrentElement,
    loadSavedElement,
    deleteSavedElement,
    renameSavedElement,
    editSavedElement,
    loadSavedOfferElements,
  } = useSavedElements({
    elements,
    pages,
    setPages,
    setElements,
    pageSettings,
    currentPageIndex,
    paginationEnabled,
    currentElementName,
    setCurrentElementName,
    isUpdatingElement,
    setIsUpdatingElement,
    selectedSavedElementId,
    setSelectedSavedElementId,
    selectedBrand,
    selectedOffer,
    brands,
    offers,
    setSavedOfferElements,
    setFilteredSavedElements,
    setStatusTitle,
    setStatusMessage,
    setShowStatusAlert,
    setShowSaveElementDialog,
    setPendingTemplateData,
    setShowReplaceConfirm,
    setShowLoadTemplateAlert,
    setLoadTemplateError,
    setSaveType,
    setIsLoadingExistingContent,
    setSelectedBrand,
    setSelectedOffer,
    loadOffersForBrand,
    setForceRender,
  });

  // Load replaced template (from confirmation dialog)
  const loadReplaceTemplate = useCallback(() => {
    if (!pendingTemplateData) return;
    const { elements: templateElements, pageSettings: templatePageSettings, pagination } = pendingTemplateData;
    setElements(templateElements);
    if (templatePageSettings) setPageSettings(templatePageSettings);
    if (pagination && pagination.enabled && pagination.pages) {
      let idx = 0;
      const rebuilt = pagination.pages.map(p => {
        const els = templateElements.slice(idx, idx + p.elementCount);
        idx += p.elementCount;
        return { id: p.id, elements: els };
      });
      setPages(rebuilt);
      setCurrentPageIndex(pagination.currentPageIndex || 0);
      setNextPageId(Math.max(...rebuilt.map(p => p.id)) + 1);
      setPaginationEnabled(true);
    } else {
      setPaginationEnabled(false);
      setPages([{ id: 1, elements: [] }]);
      setCurrentPageIndex(0);
    }
    setShowReplaceConfirm(false);
    setPendingTemplateData(null);
    setIsUpdatingElement(true);
    setSelectedSavedElementId(pendingTemplateData.id);
    setCurrentElementName(pendingTemplateData.name);
  }, [pendingTemplateData, setElements, setPageSettings, setPages, setCurrentPageIndex, setNextPageId, setPaginationEnabled, setShowReplaceConfirm, setPendingTemplateData, setIsUpdatingElement, setSelectedSavedElementId, setCurrentElementName]);

  // ── Element Renderer ──────────────────────────────────────────────────────
  const renderInlineEditableElement = useElementRenderer({
    editingElement,
    editingField,
    tempValue,
    setTempValue,
    textEditorRef,
    selectedElement,
    selectedElementContext,
    selectedSubElement,
    iconSelectorOpen,
    setIconSelectorOpen,
    isBrandsPage,
    paginationEnabled,
    currentPageIndex,
    pageSettings,
    forceRender,
    rightPanelOpen,
    setSelectedElement,
    setSelectedElementContext,
    setSelectedSubElement,
    setTabValue,
    setRightPanelOpen,
    setPages,
    setElements,
    setInsertAfterElementId,
    setPromptDialogTitle,
    setPromptDialogMessage,
    setPromptDialogValue,
    setPromptAction,
    setShowPromptDialog,
    startInlineEdit,
    saveInlineEdit,
    cancelInlineEdit,
    handleKeyPress,
    handleResizeStart,
    findAndUpdateLayout,
    moveElement,
    generateId,
    deleteElement,
    deleteElementFromZone,
    updateElement,
    applyHighlight,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    getZoneHeightStyle,
    getHeightModeText,
    toggleZoneHeight,
    getHeightTooltip,
    getExpandIcon,
    selectedVariantKey,
    setSelectedVariantKey,
    targetZone,
  });

  // ── useImperativeHandle ───────────────────────────────────────────────────
  useImperativeHandle(ref, () => ({
    loadTemplateFromImport: (templateObj) => {
      console.log('🔄 Starting template import:', templateObj);
      setIsLoadingExistingContent(true);

      const processElement = (element) => {
        const processedElement = { ...element, id: element.id || generateId() };
        if (element.type === 'heading') processedElement.type = ELEMENT_TYPES.HEADING;
        if (element.type === 'text') processedElement.type = ELEMENT_TYPES.TEXT;
        if (element.type === 'image') processedElement.type = ELEMENT_TYPES.IMAGE;
        if (element.type === 'button') processedElement.type = ELEMENT_TYPES.BUTTON;
        if (element.type === 'comparison_table') {
          processedElement.type = ELEMENT_TYPES.COMPARISON_TABLE;
        }
        if ((element.type === 'layout' || element.type === 'LAYOUT') && element.zones) {
          processedElement.type = ELEMENT_TYPES.LAYOUT;
          if (element.layoutType) processedElement.layoutType = element.layoutType.replace(/_/g, '-');
          processedElement.zones = element.zones.map(zone => ({
            ...zone,
            id: zone.id || generateId(),
            elements: (zone.elements || []).map(zoneElement => processElement(zoneElement)),
          }));
        }
        return processedElement;
      };

      const processedElements = (templateObj.elements || []).map(el => processElement(el));
      setElements(processedElements);
      setPageSettings(templateObj.pageSettings || {
        title: 'My Offer Page',
        backgroundColor: '#ffffff',
        maxWidth: '800px',
        fontFamily: 'Arial, sans-serif',
      });

      if (templateObj.pagination && templateObj.pagination.enabled) {
        const savedPagination = templateObj.pagination;
        let elementIndex = 0;
        const reconstructedPages = savedPagination.pages.map(pageInfo => {
          const pageElements = processedElements.slice(elementIndex, elementIndex + pageInfo.elementCount);
          elementIndex += pageInfo.elementCount;
          return { id: pageInfo.id, elements: pageElements };
        });
        setPages(reconstructedPages);
        setCurrentPageIndex(savedPagination.currentPageIndex || 0);
        setNextPageId(Math.max(...reconstructedPages.map(p => p.id)) + 1);
        setPaginationEnabled(true);
      } else {
        setPaginationEnabled(false);
        setPages([{ id: 1, elements: [] }]);
        setCurrentPageIndex(0);
      }
      console.log('✅ Import completed');
    },
    togglePagination,
  }));

  // ── Effects ───────────────────────────────────────────────────────────────
  useEffect(() => {
    console.log('🎯 selectedElement changed:', selectedElement);
    console.log('🎯 selectedElementContext:', selectedElementContext);
    console.log('🎯 rightPanelOpen:', rightPanelOpen);
    console.log('🎯 tabValue:', tabValue);
  }, [selectedElement, selectedElementContext, rightPanelOpen, tabValue]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (iconSelectorOpen && !event.target.closest('.icon-selector-popup')) {
        setIconSelectorOpen(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [iconSelectorOpen]);

  // Brands page: restore sharedElements into pages/pagination
  useEffect(() => {
    if (!isBrandsPage) return;
    if (sharedElements !== null && Array.isArray(sharedElements) && sharedElements.length > 0) {
      const totalElementsOnPages = pages.reduce((acc, p) => acc + (p.elements?.length || 0), 0);
      if (paginationEnabled && (pages.length > 1 || (totalElementsOnPages > 0 && totalElementsOnPages === sharedElements.length))) {
        setIsLoadingExistingContent(false);
        return;
      }
      let elementWithPagination = null;
      let savedPagination = null;
      for (const element of sharedElements) {
        if (element.content) {
          try {
            const contentData = JSON.parse(element.content);
            if (contentData.pagination && contentData.pagination.enabled) {
              elementWithPagination = element;
              savedPagination = contentData.pagination;
              break;
            }
          } catch (e) { /* not JSON */ }
        }
      }
      if (elementWithPagination && savedPagination) {
        let elementIndex = 0;
        const reconstructedPages = savedPagination.pages.map(pageInfo => {
          const pageElements = sharedElements.slice(elementIndex, elementIndex + pageInfo.elementCount);
          elementIndex += pageInfo.elementCount;
          return { id: pageInfo.id, elements: pageElements };
        });
        setPages(reconstructedPages);
        setCurrentPageIndex(savedPagination.currentPageIndex || 0);
        setNextPageId(Math.max(...reconstructedPages.map(p => p.id)) + 1);
        setPaginationEnabled(true);
      } else {
        const totalEls = pages.reduce((acc, p) => acc + (p.elements?.length || 0), 0);
        if (!(paginationEnabled && totalEls > 0)) {
          setPages([{ id: 1, elements: sharedElements }]);
          setCurrentPageIndex(0);
          setNextPageId(2);
          setPaginationEnabled(true);
        }
      }
      setIsLoadingExistingContent(false);
    }
  }, [isBrandsPage, sharedElements, pages, paginationEnabled, selectedBrand, selectedOffer]);

  useEffect(() => {
    console.log('ℹ️ Long elements check disabled - manual pagination only');
  }, []);

  useEffect(() => {
    console.log('🔄 Pagination state changed:', { paginationEnabled, pagesCount: pages.length, currentPageIndex, elementsCount: elements.length });
  }, [paginationEnabled, pages.length, currentPageIndex, elements.length]);


  // ── generateHTML ──────────────────────────────────────────────────────────
  const generateHTML = useCallback(() => {
    let elementsHTML;

    if (paginationEnabled && pages.length > 0) {
      // Pagination mode - export with page breaks
      console.log('📄 Exporting paginated content with page breaks:', {
        pagesCount: pages.length,
        totalElements: pages.reduce((total, page) => total + page.elements.length, 0)
      });

      // Generate HTML for each page separately
      const pagesHTML = pages.map((page, pageIndex) => {
        const pageElementsHTML = page.elements.map(element => {
          if (!element || !element.type) {
            console.warn('Invalid element found:', element);
            return '';
          }
          return generateElementHTML(element);
        }).filter(html => html !== '').join('\n');

        return `
        <div class="page-container" data-page="${pageIndex + 1}">
          <div class="page-header">
            <span class="page-number">Page ${pageIndex + 1} of ${pages.length}</span>
          </div>
          <div class="page-content">
            ${pageElementsHTML}
          </div>
        </div>`;
      }).join('\n');

      elementsHTML = pagesHTML;
    } else {
      // Regular mode - export current elements
      console.log('📄 Exporting single page content:', {
        elementsCount: elements.length
      });

      elementsHTML = elements.map(element => {
        if (!element || !element.type) {
          console.warn('Invalid element found:', element);
          return '';
        }
        return generateElementHTML(element);
      }).filter(html => html !== '').join('\n');
    }

    return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0, user-scalable=yes">
    <title>${pageSettings.title}</title>
    <link href="https://fonts.googleapis.com/icon?family=Material+Icons" rel="stylesheet">
    <style>
        * {
            box-sizing: border-box;
        }
        
        body {
            font-family: ${pageSettings.fontFamily};
            background-color: ${pageSettings.backgroundColor};
            margin: 0;
            padding: 0; /* Let container handle padding */
            line-height: 1.6;
            font-size: 16px;
            display: flex;
            justify-content: center;
            align-items: flex-start; /* Align to top like preview */
            min-height: 100vh;
            box-sizing: border-box;
        }
        
        /* Phone Frame Styles - Always mobile portrait */
        .phone-frame {
            width: ${STANDARD_MOBILE.displayWidth - 16}px; /* Fixed mobile width (389px) */
            min-height: ${STANDARD_MOBILE.displayHeight - 16}px; /* Mobile height (862px) */
            background-color: #fff; /* Match preview background */
            border-radius: 20px; /* Mobile frame border radius */
            padding: 8px; /* Reduced padding for more content space */
            box-shadow: 0 4px 12px rgba(0,0,0,0.15); /* Mobile frame shadow */
            position: relative;
            margin: 0 auto; /* Center like preview */
            overflow: visible; /* Allow content to breathe */
        }
        
        /* Remove phone decorative elements to match clean preview */
        
        .phone-screen {
            width: 100%;
            min-height: 100%;
            background-color: transparent; /* Let phone-frame handle background */
            border-radius: 0; /* Remove inner border radius */
            overflow: visible; /* Allow content expansion like preview */
            position: relative;
        }
        
        .offer-container {
            max-width: 100%;
            margin: 0;
            padding: 0; /* Remove padding, frame handles it */
            background: transparent; /* Frame handles background */
            box-shadow: none;
            border-radius: 0;
            height: 100%;
            overflow-y: auto;
        }
        
        .layout-container {
            display: flex;
            gap: 8px;
            margin-bottom: 4px;
            flex-direction: row; /* Side-by-side in mobile frame */
            flex-wrap: nowrap;
        }
        
        .zone {
            flex: 1;
            word-wrap: break-word;
            overflow-wrap: break-word;
            box-sizing: border-box;
            min-width: 0; /* Allow flex items to shrink below content width */
        }
        
        /* Mobile portrait optimizations */
        body {
            padding: 0;
            font-size: 14px;
        }
        
        .offer-container {
            padding: 16px; /* Match canvas exactly */
            border-radius: 0;
        }
        
        h1, h2, h3 {
            line-height: 1.3;
            margin: 0 0 2px 0;
            /* Preserve original font styles - don't force font-size */
        }
        
        p {
            line-height: 1.4;
            margin: 0 0 8px 0;
            overflow-wrap: break-word;
            /* Preserve original font styles - don't force font-size */
        }
        
        img {
            max-width: 100%;
            height: auto;
            /* Preserve original image dimensions when possible */
        }
        
        /* Ensure images in narrow columns don't become too small */
        .zone img {
            min-width: 80px;
            min-height: 60px;
        }
        
        a, button {
            display: block !important;
            text-align: center !important;
            margin: 0 0 8px 0 !important;
            border-radius: 6px !important;
            text-decoration: none !important;
            font-weight: bold !important;
            min-height: 36px !important;
            line-height: 1.2 !important;
            width: 100% !important;
            box-sizing: border-box !important;
            word-wrap: break-word !important;
            /* Preserve original padding and font-size from element styles */
        }
        
        iframe {
            width: 100% !important;
            max-width: 100% !important;
        }
        
        /* Only apply height override to iframes that are not in responsive video containers */
        iframe:not([style*="position: absolute"]) {
            height: 200px !important;
        }
        

        
        /* Desktop styles */
        @media (min-width: 1025px) {
            .layout-container {
                gap: 24px;
            }
            
            /* Only apply height override to iframes that are not in responsive video containers */
            iframe:not([style*="position: absolute"]) {
                height: 315px !important;
            }
        }
        
        /* Touch-friendly interactive elements */
        a, button {
            cursor: pointer;
            min-height: 44px;
            min-width: 44px;
        }
        
        img {
            max-width: 100%;
            height: auto;
        }
        
        /* FAQ Accordion specific styles */
        .faq-question:hover {
            background-color: rgba(0, 0, 0, 0.06) !important;
        }
        
        .faq-answer {
            transition: all 0.3s ease;
        }
        
        @media (max-width: 768px) {
            .faq-question {
                padding: 12px 8px !important;
            }
            
            .faq-answer {
                padding: 12px !important;
            }
            
            .faq-icon {
                font-size: 20px !important;
            }
        }
        
        /* Accessibility improvements */
        @media (prefers-reduced-motion: reduce) {
            * {
                animation-duration: 0.01ms !important;
                animation-iteration-count: 1 !important;
                transition-duration: 0.01ms !important;
            }
        }
        
        /* Pagination Styles */
        .page-container {
            width: 100%;
            margin-bottom: 40px;
            page-break-after: always;
            break-after: page;
            min-height: ${STANDARD_MOBILE.displayHeight - 16}px;
            display: flex;
            flex-direction: column;
        }
        
        .page-header {
            text-align: center;
            padding: 8px 0;
            margin-bottom: 16px;
            border-bottom: 2px solid #e0e0e0;
            background-color: #f5f5f5;
            border-radius: 8px 8px 0 0;
        }
        
        .page-number {
            font-size: 14px;
            font-weight: bold;
            color: #666;
            background-color: #fff;
            padding: 4px 12px;
            border-radius: 12px;
            border: 1px solid #ddd;
        }
        
        .page-content {
            flex: 1;
            padding: 16px;
            background-color: #fff;
            border-radius: 0 0 8px 8px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }
        
        /* Print styles for better page breaks */
        @media print {
            .page-container {
                page-break-after: always;
                margin-bottom: 0;
            }
            
            .page-header {
                background-color: transparent !important;
                border-bottom: 1px solid #ccc !important;
            }
        }
    </style>
    <script>
        function toggleFAQItem(faqId, index, iconType) {
            const answer = document.getElementById(faqId + '-answer-' + index);
            const icon = document.getElementById(faqId + '-icon-' + index);
            
            if (answer.style.display === 'none' || answer.style.display === '') {
                answer.style.display = 'block';
                if (icon) {
                    switch(iconType) {
                        case 'expandMore':
                            icon.innerHTML = '▲';
                            break;
                        case 'arrow':
                            icon.innerHTML = '↑';
                            break;
                        case 'arrowDrop':
                            icon.innerHTML = '▲';
                            break;
                        case 'plusMinus':
                        default:
                            icon.innerHTML = '−';
                            break;
                    }
                }
            } else {
                answer.style.display = 'none';
                if (icon) {
                    switch(iconType) {
                        case 'expandMore':
                            icon.innerHTML = '▼';
                            break;
                        case 'arrow':
                            icon.innerHTML = '↓';
                            break;
                        case 'arrowDrop':
                            icon.innerHTML = '▼';
                            break;
                        case 'plusMinus':
                        default:
                            icon.innerHTML = '+';
                            break;
                    }
                }
            }
        }
    </script>
</head>
<body>
    <div class="phone-frame">
        <div class="phone-screen">
            <div class="offer-container">
                ${elementsHTML}
            </div>
        </div>
    </div>
</body>
</html>`;
  }, [elements, pageSettings]);

  // Generate link for hosted page
  const handleGenerateLink = useCallback(async () => {
    if (elements.length === 0) return;

    setGeneratingLink(true);

    try {
      const htmlContent = generateHTML();
      const pageTitle = pageSettings.title || 'My Offer Page';

      const response = await fetch(`${API_URL}/offer-templates/host-page`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          htmlContent,
          pageTitle
        })
      });

      if (response.ok) {
        const data = await response.json();

        // Copy URL to clipboard
        await navigator.clipboard.writeText(data.url);

        // Show success message with the URL
        setStatusTitle('Success');
        setStatusMessage(`✅ Page hosted successfully!\n\n📋 URL copied to clipboard:\n${data.url}\n\nYou can share this link with anyone!`);
        setShowStatusAlert(true);
      } else {
        const errorData = await response.json();
        setStatusTitle('Error');
        setStatusMessage(`❌ Failed to generate link: ${errorData.message || 'Unknown error'}`);
        setShowStatusAlert(true);
      }
    } catch (error) {
      console.error('Error generating link:', error);
      setStatusTitle('Error');
      setStatusMessage('❌ Failed to generate link. Please try again.');
      setShowStatusAlert(true);
    } finally {
      setGeneratingLink(false);
    }
  }, [elements, generateHTML, pageSettings.title]);

  // Standard mobile preview styles (405×878 display, represents 1080×2340 logical)

  // ── Preview / Canvas styles ───────────────────────────────────────────────
  const getPreviewStyles = () => {
    switch (previewDevice) {
      case 'mobile':
        const mobileWidth = isBrandsPage ? BRANDS_EDITOR_DEVICE_WIDTH : STANDARD_MOBILE.displayWidth;
        const mobileHeight = isBrandsPage ? BRANDS_EDITOR_DEVICE_HEIGHT : STANDARD_MOBILE.displayHeight;

        return {
          width: `${mobileWidth}px`,
          height: `${mobileHeight}px`,
          margin: '0 auto',
          backgroundColor: '#1a1a1a', // Phone frame
          borderRadius: '15px', // Scaled down rounded corners
          padding: `${STANDARD_MOBILE.displaySafeAreaTop}px 8px ${STANDARD_MOBILE.displaySafeAreaBottom}px 8px`, // Scaled safe areas
          boxShadow: '0 8px 16px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.1)', // Scaled shadow
          position: 'relative',
          overflow: 'hidden',
          border: '2px solid #333', // Phone frame border
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '12px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '120px', // Realistic notch width
            height: '6px',
            backgroundColor: '#1a1a1a',
            borderRadius: '3px',
            zIndex: 1,
            boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.5)'
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: '12px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '120px',
            height: '4px',
            backgroundColor: '#1a1a1a',
            borderRadius: '2px',
            zIndex: 1
          }
        };
      case 'desktop':
        return {
          width: '400px', // Realistic desktop width
          height: '600px', // Realistic desktop height
          margin: '0 auto',
          backgroundColor: '#fff',
          borderRadius: '12px',
          border: '1px solid #e0e0e0',
          boxShadow: '0 8px 24px rgba(0,0,0,0.2), 0 2px 8px rgba(0,0,0,0.1)', // Realistic desktop shadow
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '0',
            left: '0',
            right: '0',
            height: '32px',
            backgroundColor: '#f8f9fa',
            borderBottom: '1px solid #e0e0e0',
            display: 'flex',
            alignItems: 'center',
            paddingLeft: '16px',
            '&::after': {
              content: '""',
              display: 'flex',
              gap: '8px'
            }
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: '8px',
            left: '16px',
            display: 'flex',
            gap: '8px',
            '&::before': {
              content: '""',
              width: '14px',
              height: '14px',
              borderRadius: '50%',
              backgroundColor: '#ff5f56',
              boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.2)'
            },
            '&::after': {
              content: '""',
              width: '14px',
              height: '14px',
              borderRadius: '50%',
              backgroundColor: '#ffbd2e',
              boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.2)'
            }
          }
        };
      case 'tablet':
        return {
          width: '480px', // iPad width
          height: '640px', // iPad height
          margin: '0 auto',
          backgroundColor: '#1a1a1a', // Dark tablet frame
          borderRadius: '24px', // Tablet rounded corners
          padding: '16px 12px', // Tablet bezels
          boxShadow: '0 16px 32px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.1)', // Realistic tablet shadow
          position: 'relative',
          overflow: 'hidden',
          border: '2px solid #333', // Tablet frame border
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '8px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '80px', // Tablet camera
            height: '4px',
            backgroundColor: '#1a1a1a',
            borderRadius: '2px',
            zIndex: 1
          }
        };
      default: // desktop
        return { maxWidth: '100%' };
    }
  };

  // Get canvas styles with standard mobile frame (375×667 display, or compact size for brands page)
  const getCanvasStyles = () => {
    if (previewDevice === 'mobile') {
      return {
        backgroundColor: '#fff', // Match preview white background
        borderRadius: 0, // Match preview (no border radius)
        overflow: 'hidden', // No scrollbar
        minHeight: isBrandsPage ? `${BRANDS_EDITOR_DEVICE_HEIGHT}px` : '667px',
        height: isBrandsPage ? '100%' : '667px',
        width: isBrandsPage ? '100%' : '375px',
        position: 'relative',
        margin: isBrandsPage ? '0 auto' : 0,
        transform: isBrandsPage ? 'scale(1)' : 'scale(0.827)',
        transformOrigin: isBrandsPage ? 'center center' : 'top left',
        display: 'flex',
        flexDirection: 'column'
      };
    } else if (previewDevice === 'tablet') {
      return {
        backgroundColor: pageSettings.backgroundColor,
        borderRadius: '24px', // Match the tablet frame
        overflow: 'visible', // Allow content to expand without scrolling
        minHeight: '640px', // Minimum height to match frame, but can expand
        height: 'auto', // Allow dynamic height expansion
        width: '480px', // Match the tablet frame width
        position: 'relative',
        margin: '0 auto', // Center the canvas
        transform: 'scale(1)', // Ensure no additional scaling
        transformOrigin: 'center center',
        display: 'flex',
        flexDirection: 'column'
      };
    } else if (previewDevice === 'desktop') {
      return {
        backgroundColor: pageSettings.backgroundColor,
        borderRadius: '12px', // Match the desktop frame
        overflow: 'visible', // Allow content to expand without scrolling
        minHeight: '600px', // Minimum height to match frame, but can expand
        height: 'auto', // Allow dynamic height expansion
        width: '400px', // Match the desktop frame width
        position: 'relative',
        margin: '0 auto', // Center the canvas
        transform: 'scale(1)', // Ensure no additional scaling
        transformOrigin: 'center center',
        border: '1px solid #e0e0e0',
        boxShadow: '0 8px 24px rgba(0,0,0,0.2)', // Match the desktop shadow
        display: 'flex',
        flexDirection: 'column'
      };
    }
    return {};
  };

  // Mobile toolbar component

  // ── Element library definitions ──────────────────────────────────────────
  const elementLibrary = [
    { type: ELEMENT_TYPES.HEADING, icon: TitleIcon, label: 'Heading', desc: 'Main titles and headers' },
    { type: ELEMENT_TYPES.TEXT, icon: TextIcon, label: 'Text', desc: 'Paragraphs and descriptions' },
    { type: ELEMENT_TYPES.BUTTON, icon: ButtonIcon, label: 'Button', desc: 'Call-to-action buttons' },
    { type: ELEMENT_TYPES.FITTABLE_IMAGE, icon: ImageIcon, label: 'Fittable Image', desc: 'Upload image and position it within a fixed frame' },
    { type: ELEMENT_TYPES.VIDEO, icon: VideoIcon, label: 'Video', desc: 'YouTube and video embeds' },
    { type: ELEMENT_TYPES.VIDEO_WITH_BUTTON_OVERLAY, icon: VideoIcon, label: 'Video with button overlay', desc: 'Video with interactive clickable buttons' },

    { type: ELEMENT_TYPES.QUOTATION_BLOCK, icon: TextIcon, label: 'Quotation Block', desc: 'Stylish quote/testimonial block with light pink background' },
    { type: ELEMENT_TYPES.CHECKLIST, icon: VerifiedIcon, label: 'Checklist' },

    { type: ELEMENT_TYPES.CUSTOM_LIST, icon: ListIcon, label: 'Custom List', desc: 'List with icons, editable rows, and background' },
    { type: ELEMENT_TYPES.CUSTOM_LIST_TWO_LINE, icon: ListIcon, label: 'Two-Line List', desc: 'List with icons, titles, and descriptions - perfect for feature lists' },
    { type: ELEMENT_TYPES.TIMELINE_LIST, icon: TimelineIcon, label: 'Timeline List', desc: 'Step-by-step timeline with stages and descriptions' },
    { type: ELEMENT_TYPES.PROCESS_STEPS, icon: TimelineIcon, label: 'Process Steps', desc: 'Visual step-by-step process with numbered circles and images' },

    { type: ELEMENT_TYPES.TEXT_WITH_RIGHT_IMAGE, icon: ImageIcon, label: 'Text + Right Image', desc: 'Text content with image positioned on the right' },
    { type: ELEMENT_TYPES.LEFT_IMAGE_TEXT, icon: ImageIcon, label: 'Left Image + Text', desc: 'Circular image on left with editable text on right' },
    { type: ELEMENT_TYPES.CIRCULAR_IMAGE, icon: ImageIcon, label: 'Circular Image', desc: 'Upload and crop a circular image' },
    { type: ELEMENT_TYPES.CROPPABLE_IMAGE, icon: ImageIcon, label: 'Croppable Image', desc: 'Upload and crop rectangular images' },

    { type: ELEMENT_TYPES.FAQ_ACCORDION, icon: InfoIcon, label: 'FAQ Accordion', desc: 'Expandable FAQ/accordion list' },
    { type: ELEMENT_TYPES.COMPARISON_TABLE, icon: InfoIcon, label: 'Comparison Table', desc: '3-column comparison table' },
    { type: ELEMENT_TYPES.TWO_COLUMN_COMPARISON, icon: InfoIcon, label: 'Two Column Comparison', desc: 'Side-by-side comparison with pros/cons' },
    { type: ELEMENT_TYPES.STAR_RATING, icon: StarIcon, label: 'Star Rating', desc: '5 star icons list for reviewing' },
    { type: ELEMENT_TYPES.STAR_RATING_TABLE, icon: StarIcon, label: 'Star Rating Table', desc: 'List of ratings with labels side-by-side' },
    { type: ELEMENT_TYPES.IMAGE_OVERLAY, icon: ImageIcon, label: 'Image w/ Overlay', desc: 'An image with a floating container on top for other elements' },
  ];

  // Custom Elements Library - Grouped structure from the image
  const customElementsLibrary = useMemo(() => [
    {
      id: 'faq-accordion',
      label: 'common questions',
      desc: 'Expandable FAQ/accordion list',
      icon: InfoIcon,
      category: 'FAQ',
      elements: [DEFAULT_ELEMENTS[ELEMENT_TYPES.FAQ_ACCORDION]]
    },
    // Buttons Group
    {
      id: 'button-with-notice',
      label: 'Button w/notice',
      desc: 'Button with notice text',
      icon: ButtonIcon,
      category: 'Buttons',
      elements: buttonNoticeTemplates.length > 0 ? [
        {
          id: generateId(),
          type: 'BUTTON_WITH_NOTICE',
          variant: 'default',
          styleVariant: 'default',
          variants: buttonNoticeTemplates.reduce((acc, template) => {
            const variant = template.elements[0]?.variant || template.name.toLowerCase().replace(/\s+/g, '-');
            acc[variant] = template.elements[0]?.content || {};
            return acc;
          }, {}),
          content: {
            notice: 'Special Notice',
            callToAction: 'Take action now!',
            buttonText: 'Click Here',
            buttonUrl: '#'
          },
          styles: {
            backgroundColor: '#fce4ec',
            padding: '20px',
            borderRadius: '8px',
            marginBottom: '20px',
            textAlign: 'center'
          }
        }
      ] : [
        {
          id: generateId(),
          type: 'BUTTON_WITH_NOTICE',
          variant: 'default',
          styleVariant: 'default',
          variants: {},
          content: {
            notice: 'Loading templates...',
            callToAction: 'Please wait...',
            buttonText: 'Loading...',
            buttonUrl: '#'
          },
          styles: {
            backgroundColor: '#f5f5f5',
            padding: '20px',
            borderRadius: '8px',
            marginBottom: '20px',
            textAlign: 'center'
          }
        }
      ]
    },
    {
      id: 'button-with-image-callout',
      label: 'Button w/Image Call out',
      desc: 'Button with image and callout badge',
      icon: ButtonIcon,
      category: 'Buttons',
      elements: buttonImageCalloutTemplates.length > 0 ? [
        {
          id: generateId(),
          type: 'BUTTON_IMAGE_CALLOUT',
          variant: 'default',
          variants: buttonImageCalloutTemplates.reduce((acc, template) => {
            const variant = template.elements[0]?.variant || template.name.toLowerCase().replace(/\s+/g, '-');
            acc[variant] = template.elements[0]?.content || {};
            return acc;
          }, {}),
          content: {
            headline: 'Button w/Image Call out',
            image: '/api/placeholder/400/300',
            badge: '',
            badgeColor: '#4caf50',
            buttonText: 'Click Here',
            buttonUrl: '#',
            buttonColor: '#4caf50',
            subheadline: '',
            subheadlineIcon: '',
            background: '#ffffff',
            textColor: '#333333'
          },
          styles: {
            borderRadius: '16px',
            marginBottom: '20px',
            textAlign: 'center'
          }
        }
      ] : [
        {
          id: generateId(),
          type: 'BUTTON_IMAGE_CALLOUT',
          variant: 'default',
          variants: {},
          content: {
            headline: 'Loading templates...',
            image: '/api/placeholder/400/300',
            badge: '',
            badgeColor: '',
            buttonText: 'Loading...',
            buttonUrl: '#',
            buttonColor: '#4caf50',
            subheadline: '',
            subheadlineIcon: '',
            background: '#f5f5f5',
            textColor: '#333333'
          },
          styles: {
            borderRadius: '16px',
            marginBottom: '20px',
            textAlign: 'center'
          }
        }
      ]
    },

    {
      id: 'button-with-text',
      label: 'Button w/text',
      desc: 'Single button with text and style variants',
      icon: ButtonIcon,
      category: 'Buttons',
      elements: buttonTextTemplates.length > 0 ? [
        {
          id: generateId(),
          type: 'BUTTON_TEXT_VARIANT',
          variant: 'default',
          variants: buttonTextTemplates.reduce((acc, template) => {
            const variant = template.elements[0]?.variant || template.name.toLowerCase().replace(/\s+/g, '-');
            acc[variant] = template.elements[0]?.content || {};
            return acc;
          }, {}),
          content: {
            buttonText: 'Button Text',
            buttonUrl: '#',
            buttonColor: '#4caf50',
            textColor: '#ffffff',
            fontWeight: 'normal',
            fontSize: '1rem',
            borderRadius: '8px',
            boxShadow: 'none',
            icon: '',
            iconPosition: 'none',
            padding: '16px 32px',
            width: 'auto',
            margin: '0 auto'
          },
          styles: {
            marginBottom: '20px',
            textAlign: 'center'
          }
        }
      ] : [
        {
          id: generateId(),
          type: 'BUTTON_TEXT_VARIANT',
          variant: 'default',
          variants: {},
          content: {
            buttonText: 'Loading...',
            buttonUrl: '#',
            buttonColor: '#f5f5f5',
            textColor: '#333333',
            fontWeight: 'normal',
            fontSize: '1rem',
            borderRadius: '8px',
            boxShadow: 'none',
            icon: '',
            iconPosition: 'none',
            padding: '16px 32px',
            width: 'auto',
            margin: '0 auto'
          },
          styles: {
            marginBottom: '20px',
            textAlign: 'center'
          }
        }
      ]
    },
    {
      id: 'button-headline-product-image',
      label: 'Button + headline + Product Image',
      desc: 'Complete button section with headline and product image',
      icon: ButtonIcon,
      category: 'Buttons',
      elements: [
        {
          id: generateId(),
          type: 'BUTTON_HEADLINE_PRODUCT_IMAGE',
          variant: 'default',
          variants: {},
          content: {
            headline: 'Amazing Product Offer',
            productImage: 'https://images.unsplash.com/photo-1584361853901-dd1904265ba8?w=400&h=300&fit=crop&q=80',
            buttonText: 'Get This Offer Now!',
            buttonUrl: '#',
            buttonColor: '#4caf50',
            subheadline: 'Limited time offer - Don\'t miss out!',
            background: '#ffffff',
            textColor: '#333333'
          },
          styles: {
            borderRadius: '16px',
            marginBottom: '20px',
            textAlign: 'center',
            padding: '20px'
          }
        }
      ]
    },

    // Lists Group
    {
      id: 'list-bad-things',
      label: 'List - Bad things',
      desc: 'List of negative points',
      icon: TextIcon,
      category: 'Lists',
      elements: [
        {
          type: 'text',
          content: '• Expensive compared to alternatives',
          styles: { fontSize: '16px', color: '#d32f2f', marginBottom: '8px' }
        },
        {
          type: 'text',
          content: '• Not available in all regions',
          styles: { fontSize: '16px', color: '#d32f2f', marginBottom: '8px' }
        },
        {
          type: 'text',
          content: '• May cause side effects in rare cases',
          styles: { fontSize: '16px', color: '#d32f2f', marginBottom: '8px' }
        }
      ]
    },
    {
      id: 'list-check-boxes',
      label: 'List - Check boxes',
      desc: 'List with checkboxes',
      icon: VerifiedIcon,
      category: 'Lists',
      elements: [
        {
          type: 'checklist',
          title: 'What you get:',
          items: [
            { icon: 'verified', text: 'Free shipping' },
            { icon: 'verified', text: '30-day money-back guarantee' },
            { icon: 'verified', text: '24/7 customer support' }
          ],
          styles: { marginBottom: '20px' }
        }
      ]
    },
    {
      id: 'list-numbered',
      label: 'List - Numbered',
      desc: 'Numbered list',
      icon: TextIcon,
      category: 'Lists',
      elements: [
        {
          type: 'text',
          content: '1. Step one: Sign up for an account',
          styles: { fontSize: '16px', color: '#333', marginBottom: '8px' }
        },
        {
          type: 'text',
          content: '2. Step two: Choose your plan',
          styles: { fontSize: '16px', color: '#333', marginBottom: '8px' }
        },
        {
          type: 'text',
          content: '3. Step three: Enjoy the benefits',
          styles: { fontSize: '16px', color: '#333', marginBottom: '8px' }
        }
      ]
    },
    {
      id: 'list-steps-how-to',
      label: 'List - steps/how to',
      desc: 'Step-by-step instructions',
      icon: TextIcon,
      category: 'Lists',
      elements: [
        {
          type: 'text',
          content: 'Step 1: Open the box',
          styles: { fontSize: '16px', color: '#1976d2', marginBottom: '8px' }
        },
        {
          type: 'text',
          content: 'Step 2: Remove the product',
          styles: { fontSize: '16px', color: '#1976d2', marginBottom: '8px' }
        },
        {
          type: 'text',
          content: 'Step 3: Follow the instructions',
          styles: { fontSize: '16px', color: '#1976d2', marginBottom: '8px' }
        }
      ]
    },

    // Icons Group
    {
      id: 'feature-icons',
      label: 'Feature Icons',
      desc: 'Icons for features',
      icon: VerifiedIcon,
      category: 'Icons',
      elements: [{
        type: 'icon',
        icon: 'verified',
        styles: { color: '#1976d2', fontSize: '32px' }
      }
      ]
    },
    {
      id: 'features-guaranteed-service-icons',
      label: 'Features - Guaranteed Service Icons',
      desc: 'Service guarantee icons',
      icon: VerifiedIcon,
      category: 'Icons',
      elements: [
        {
          type: 'icon',
          icon: 'verified',
          styles: { color: '#43a047', fontSize: '32px' }
        }
      ]
    },


    // Features Group
    {
      id: 'features-header-paragraph-expandable',
      label: 'Features - Header + paragraph + expandable',
      desc: 'Expandable feature section',
      icon: TextIcon,
      category: 'Features',
      elements: [
        {
          type: 'heading',
          content: 'Product Features',
          styles: { fontSize: '24px', fontWeight: 'bold', color: '#333', marginBottom: '16px' }
        },
        {
          type: 'text',
          content: 'Discover the key features that make our product stand out from the competition.',
          styles: { fontSize: '16px', color: '#666', marginBottom: '20px' }
        },
        {
          type: 'text',
          content: '✓ Premium Quality Materials',
          styles: { fontSize: '14px', color: '#333', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }
        },
        {
          type: 'text',
          content: '✓ Advanced Technology',
          styles: { fontSize: '14px', color: '#333', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }
        },
        {
          type: 'text',
          content: '✓ Lifetime Warranty',
          styles: { fontSize: '14px', color: '#333', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }
        }
      ]
    },
    {
      id: 'features-media-awards',
      label: 'Features - Media Awards',
      desc: 'Media awards section',
      icon: ImageIcon,
      category: 'Features',
      elements: [
        {
          type: 'heading',
          content: 'Award-Winning Product',
          styles: { fontSize: '24px', fontWeight: 'bold', color: '#333', marginBottom: '16px', textAlign: 'center' }
        },
        {
          type: 'text',
          content: '🏆 Best Product 2024',
          styles: { fontSize: '18px', color: '#1976d2', marginBottom: '8px', textAlign: 'center', fontWeight: 'bold' }
        },
        {
          type: 'text',
          content: '⭐ 5-Star Rating from TechReview',
          styles: { fontSize: '16px', color: '#666', marginBottom: '8px', textAlign: 'center' }
        },
        {
          type: 'text',
          content: '🎯 Editor\'s Choice Award',
          styles: { fontSize: '16px', color: '#666', marginBottom: '20px', textAlign: 'center' }
        }
      ]
    },
    {
      id: 'ingredients',
      label: 'Ingredients',
      desc: 'Product ingredients list',
      icon: TextIcon,
      category: 'Features',
      elements: [
        {
          type: 'heading',
          content: 'Natural Ingredients',
          styles: { fontSize: '24px', fontWeight: 'bold', color: '#333', marginBottom: '16px' }
        },
        {
          type: 'text',
          content: '🌿 Organic Extract - 100mg',
          styles: { fontSize: '14px', color: '#333', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }
        },
        {
          type: 'text',
          content: '💧 Pure Water - 50ml',
          styles: { fontSize: '14px', color: '#333', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }
        },
        {
          type: 'text',
          content: '🌱 Natural Preservatives',
          styles: { fontSize: '14px', color: '#333', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }
        },
        {
          type: 'text',
          content: '🚫 No Artificial Additives',
          styles: { fontSize: '14px', color: '#333', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }
        }
      ]
    },

    // Images Group
    {
      id: 'image-headline-text-bullets-button',
      label: 'Image + headline + text + bullets + Button',
      desc: 'Image with headline, supporting text, bullets, and a CTA button',
      icon: ImageIcon,
      category: 'Images',
      elements: [
        {
          type: 'image',
          content: 'https://via.placeholder.com/600x350?text=Your+Image',
          alt: 'Featured Image',
          styles: { width: '100%', height: 'auto', borderRadius: '8px', marginBottom: '16px' }
        },
        {
          type: 'heading',
          content: 'Compelling Headline Goes Here',
          styles: { fontSize: '22px', fontWeight: 'bold', color: '#333', marginBottom: '8px', textAlign: 'left' }
        },
        {
          type: 'text',
          content: 'Short supporting paragraph that explains the value proposition in one or two sentences.',
          styles: { fontSize: '16px', color: '#555', marginBottom: '12px', textAlign: 'left' }
        },
        {
          type: 'checklist',
          title: '',
          items: [
            { icon: 'verified', text: 'Key benefit number one' },
            { icon: 'verified', text: 'Another strong selling point' },
            { icon: 'verified', text: 'Short and easy to scan bullet' }
          ],
          styles: { marginBottom: '16px' }
        },
        {
          type: 'button',
          content: 'Get Started',
          url: '#',
          styles: { backgroundColor: '#1976d2', color: '#fff', padding: '12px 20px', borderRadius: '8px', border: 'none' }
        }
      ]
    },

    // Videos Group
    {
      id: 'video-headline-text-bullets-button',
      label: 'Video + headline + text + bullets + Button',
      desc: 'Video with headline, supporting text, bullets, and a CTA button',
      icon: VideoIcon,
      category: 'Videos',
      elements: [
        {
          type: 'video',
          content: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          styles: { width: '100%', height: '315px', marginBottom: '16px' }
        },
        {
          type: 'heading',
          content: 'Watch How It Works',
          styles: { fontSize: '22px', fontWeight: 'bold', color: '#333', marginBottom: '8px', textAlign: 'left' }
        },
        {
          type: 'text',
          content: 'A short description about what the viewer will learn or see in the video.',
          styles: { fontSize: '16px', color: '#555', marginBottom: '12px', textAlign: 'left' }
        },
        {
          type: 'checklist',
          title: '',
          items: [
            { icon: 'verified', text: 'Quick overview of the product' },
            { icon: 'verified', text: 'Real results and use cases' },
            { icon: 'verified', text: 'Clear next steps' }
          ],
          styles: { marginBottom: '16px' }
        },
        {
          type: 'button',
          content: 'Try It Now',
          url: '#',
          styles: { backgroundColor: '#1976d2', color: '#fff', padding: '12px 20px', borderRadius: '8px', border: 'none' }
        }
      ]
    },

    // Video with Button Overlay
    {
      id: 'video-single-button-overlay',
      label: 'Single button overlay',
      desc: 'Video with a single clickable button overlaid',
      icon: VideoIcon,
      category: 'Video with button overlay',
      elements: [
        {
          id: generateId(),
          type: ELEMENT_TYPES.VIDEO_WITH_BUTTON_OVERLAY,
          content: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          videoFile: '',
          thumbnailUrl: '',
          buttons: [
            {
              id: 'button-1',
              text: 'Shop Now',
              url: '#',
              icon: 'shopping_cart',
              position: 'bottom-right',
              customX: 50,
              customY: 50,
              backgroundColor: '#ff0050',
              textColor: '#ffffff',
              size: 'medium',
              borderRadius: '24px',
              padding: '12px 24px',
              fontSize: '14px',
              fontWeight: 'bold'
            }
          ],
          styles: {
            width: '100%',
            height: '500px',
            marginBottom: '16px'
          }
        }
      ]
    },
    {
      id: 'video-multiple-buttons-overlay',
      label: 'Multiple buttons overlay',
      desc: 'Video with multiple clickable buttons overlaid',
      icon: VideoIcon,
      category: 'Video with button overlay',
      elements: [
        {
          id: generateId(),
          type: ELEMENT_TYPES.VIDEO_WITH_BUTTON_OVERLAY,
          content: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          videoFile: '',
          thumbnailUrl: '',
          buttons: [
            {
              id: 'button-1',
              text: 'Shop Now',
              url: '#',
              icon: 'shopping_cart',
              position: 'bottom-right',
              customX: 85,
              customY: 85,
              backgroundColor: '#ff0050',
              textColor: '#ffffff',
              size: 'medium',
              borderRadius: '24px',
              padding: '12px 24px',
              fontSize: '14px',
              fontWeight: 'bold'
            },
            {
              id: 'button-2',
              text: 'Learn More',
              url: '#',
              icon: 'info',
              position: 'top-right',
              customX: 85,
              customY: 15,
              backgroundColor: '#2196f3',
              textColor: '#ffffff',
              size: 'small',
              borderRadius: '20px',
              padding: '8px 16px',
              fontSize: '12px',
              fontWeight: 'bold'
            },
            {
              id: 'button-3',
              text: 'Save',
              url: '#',
              icon: 'favorite',
              position: 'top-left',
              customX: 15,
              customY: 15,
              backgroundColor: '#ffffff',
              textColor: '#ff0050',
              size: 'small',
              borderRadius: '50%',
              padding: '10px',
              fontSize: '12px',
              fontWeight: 'bold'
            }
          ],
          styles: {
            width: '100%',
            height: '500px',
            marginBottom: '16px'
          }
        }
      ]
    },
    {
      id: 'video-interactive-hotspots',
      label: 'Interactive hotspots',
      desc: 'Video with interactive hotspot buttons',
      icon: VideoIcon,
      category: 'Video with button overlay',
      elements: [
        {
          id: generateId(),
          type: ELEMENT_TYPES.VIDEO_WITH_BUTTON_OVERLAY,
          content: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          videoFile: '',
          thumbnailUrl: '',
          buttons: [
            {
              id: 'button-1',
              text: '1',
              url: '#product1',
              icon: '',
              position: 'custom',
              customX: 30,
              customY: 40,
              backgroundColor: '#ffffff',
              textColor: '#000000',
              size: 'small',
              borderRadius: '50%',
              padding: '10px',
              fontSize: '12px',
              fontWeight: 'bold'
            },
            {
              id: 'button-2',
              text: '2',
              url: '#product2',
              icon: '',
              position: 'custom',
              customX: 70,
              customY: 60,
              backgroundColor: '#ffffff',
              textColor: '#000000',
              size: 'small',
              borderRadius: '50%',
              padding: '10px',
              fontSize: '12px',
              fontWeight: 'bold'
            }
          ],
          styles: {
            width: '100%',
            height: '500px',
            marginBottom: '16px'
          }
        }
      ]
    },

    // Testimonial Group
    {
      id: 'testimonial-headline-images',
      label: 'Testimonial - headline + customer images',
      desc: 'Testimonial section with headline and customer images',
      icon: TextIcon,
      category: 'Testimonial',
      elements: [
        {
          type: 'heading',
          content: 'What Our Customers Say',
          styles: { fontSize: '22px', fontWeight: 'bold', color: '#1976d2', marginBottom: '12px' }
        },
        {
          type: 'image',
          src: 'https://randomuser.me/api/portraits/men/32.jpg',
          styles: { width: '48px', height: '48px', borderRadius: '50%', marginRight: '8px' }
        },
        {
          type: 'text',
          content: '“This product changed my life!”',
          styles: { fontSize: '16px', color: '#333', fontStyle: 'italic', marginBottom: '8px' }
        }
      ]
    },
    {
      id: 'testimonial-single',
      label: 'Testimonial - single',
      desc: 'Single testimonial block',
      icon: TextIcon,
      category: 'Testimonial',
      elements: [
        {
          type: 'text',
          content: '“Absolutely love it! Highly recommend.”',
          styles: { fontSize: '16px', color: '#333', fontStyle: 'italic', marginBottom: '8px' }
        },
        {
          type: 'text',
          content: '- Jane Doe',
          styles: { fontSize: '14px', color: '#888', marginBottom: '8px' }
        }
      ]
    },
    // Comparison Group
    {
      id: 'comparison-competitor',
      label: 'comparison w competitor',
      desc: 'Comparison with competitor',
      icon: TextIcon,
      category: 'Comparison',
      elements: [
        {
          type: 'heading',
          content: 'How We Compare',
          styles: { fontSize: '20px', fontWeight: 'bold', color: '#1976d2', marginBottom: '10px' }
        },
        {
          type: 'text',
          content: 'Our Product vs. Competitor',
          styles: { fontSize: '16px', color: '#333', marginBottom: '8px' }
        }
      ]
    },
    // Highlight block Group
    {
      id: 'highlight-pull-quote',
      label: 'pull quote',
      desc: 'Highlighted pull quote',
      icon: TextIcon,
      category: 'Highlight block',
      elements: [
        {
          type: 'text',
          content: '“A must-have for everyone!”',
          styles: { fontSize: '18px', color: '#fff', backgroundColor: '#1976d2', padding: '12px', borderRadius: '8px', fontStyle: 'italic', marginBottom: '8px' }
        }
      ]
    },
    {
      id: 'headline-highlight-bg',
      label: 'Headline w/ highlight bg color, Subheadline',
      desc: 'Headline with highlighted background and subheadline',
      icon: TextIcon,
      category: 'Headline/Subheadline',
      elements: [
        {
          type: 'heading',
          content: 'Stand Out Headline',
          styles: { fontSize: '22px', fontWeight: 'bold', color: '#fff', backgroundColor: '#f5003c', padding: '8px', borderRadius: '6px', marginBottom: '8px' }
        },
        {
          type: 'text',
          content: 'Subheadline goes here',
          styles: { fontSize: '16px', color: '#333', marginBottom: '8px' }
        }
      ]
    },
    {
      id: 'subheadline-paragraph',
      label: 'subheadline + paragraph text',
      desc: 'Subheadline with paragraph text',
      icon: TextIcon,
      category: 'Headline/Subheadline',
      elements: [
        {
          type: 'text',
          content: 'Subheadline',
          styles: { fontSize: '16px', fontWeight: 'bold', color: '#1976d2', marginBottom: '4px' }
        },
        {
          type: 'text',
          content: 'This is a supporting paragraph for the subheadline.',
          styles: { fontSize: '15px', color: '#333' }
        }
      ]
    },
    {
      id: 'subheadline-highlight-bg',
      label: 'Subheadline w/ highlight bg color',
      desc: 'Subheadline with highlighted background color',
      icon: TextIcon,
      category: 'Headline/Subheadline',
      elements: [
        {
          type: 'text',
          content: 'Subheadline Highlighted',
          styles: { fontSize: '16px', color: '#fff', backgroundColor: '#1976d2', padding: '6px', borderRadius: '6px' }
        }
      ]
    },
  ], [buttonImageCalloutTemplates, buttonNoticeTemplates, buttonTextTemplates]);

  // Get unique categories from customElementsLibrary
  const customCategories = Array.from(new Set(customElementsLibrary.map(item => item.category)));


  // ── addCustomElement + element library ───────────────────────────────────
  const addCustomElement = useCallback((customElement) => {
    console.log('🎯 addCustomElement called with:', customElement);
    let newElements = customElement.elements.map(element => ({
      ...element,
      id: generateId() // Ensure unique IDs
    }));

    // Auto-populate offer link if selected
    if (selectedOffer && offers.length > 0) {
      const selectedOfferObj = offers.find(o => o._id === selectedOffer);
      if (selectedOfferObj && selectedOfferObj.purchaseUrl && selectedOfferObj.purchaseUrl !== '#' && selectedOfferObj.purchaseUrl !== '') {
        console.log('🔗 Auto-populating offer link for new custom element:', selectedOfferObj.purchaseUrl);
        newElements = populateOfferLinkInElements(newElements, selectedOfferObj.purchaseUrl);
      }
    }

    console.log('🎯 Setting elements to:', newElements);
    console.log('🎯 Using shared state:', setSharedElements !== null);
    // Reset the canvas to only the new custom element
    setElements(newElements);
  }, [setElements, selectedOffer, offers, populateOfferLinkInElements]);


  // Template selection only mode
  if (templateSelectionOnly) {
    // Show only type and subtype (for top section)
    if (typeAndSubtypeOnly) {
      return (
        <Box>
          {!hideCustomElements && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, height: '100%' }}>
              {/* Row 0: Brand and Offer Selection */}
              <Box sx={{ borderBottom: '1px solid #e0e0e0', pb: 1.5 }}>
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1.5
                }}>
                  <Typography variant="subtitle2" sx={{
                    color: 'primary.main',
                    fontWeight: 600,
                    fontSize: '0.9rem'
                  }}>
                    Brand & Offer
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={() => {
                      // Reset all states
                      setSelectedBrand('');
                      setSelectedOffer('');
                      setSelectedCustomCategory(null);
                      setSelectedCustomSubtype(null);
                      setSelectedTemplateId(null);
                      setSelectedVariantKey(null);

                      // Also reset filters if any
                      setFilteredTemplates([]);

                      // Reset Canvas content
                      if (setSharedPages) {
                        setSharedPages([{ id: 1, elements: [] }]);
                      }
                      if (setSharedElements) {
                        setSharedElements([]);
                      }

                      // Reset local pagination and elements
                      setPages([{ id: 1, elements: [] }]);
                      setCurrentPageIndex(0);
                      setPaginationEnabled(true);
                      setElements([]);
                    }}
                    sx={{
                      fontSize: '0.7rem',
                      py: 0.2,
                      minHeight: 0,
                      textTransform: 'none'
                    }}
                  >
                    New Element
                  </Button>
                </Box>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5
                }}>
                  {/* Brand Selection */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                    <Typography variant="caption" sx={{
                      fontWeight: 600,
                      color: 'text.secondary',
                      minWidth: '50px'
                    }}>
                      Brand:
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 120, flex: 1 }}>
                      <Select
                        value={selectedBrand}
                        onChange={(e) => {
                          console.log('🏷️ Brand selection changed to:', e.target.value);
                          handleBrandChange(e.target.value);
                        }}
                        displayEmpty
                        disabled={loadingBrands}
                        sx={{ fontSize: '0.8rem' }}
                      >
                        <MenuItem value="" disabled>
                          <em>{loadingBrands ? 'Loading...' : 'Select Brand'}</em>
                        </MenuItem>
                        {brands.map(brand => (
                          <MenuItem key={brand._id} value={brand._id}>
                            {brand.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Offer Selection */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                    <Typography variant="caption" sx={{
                      fontWeight: 600,
                      color: 'text.secondary',
                      minWidth: '50px'
                    }}>
                      Offer:
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 120, flex: 1 }}>
                      <Select
                        value={selectedOffer}
                        onChange={(e) => {
                          console.log('🎯 Offer selection changed to:', e.target.value);
                          setSelectedOffer(e.target.value);
                        }}
                        displayEmpty
                        disabled={!selectedBrand || loadingOffers}
                        sx={{ fontSize: '0.8rem' }}
                      >
                        <MenuItem value="" disabled>
                          <em>
                            {!selectedBrand ? 'Select Brand First' :
                              loadingOffers ? 'Loading...' : 'Select Offer'}
                          </em>
                        </MenuItem>
                        {offers.map(offer => (
                          <MenuItem key={offer._id} value={offer._id}>
                            {offer.title}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                </Box>
              </Box>

              {/* Row 1: Offer Element type */}
              <Box sx={{ borderBottom: '1px solid #e0e0e0', pb: 1.5 }}>
                <Typography variant="subtitle2" sx={{
                  mb: 1.5,
                  color: 'primary.main',
                  fontWeight: 600,
                  textAlign: 'left',
                  fontSize: '0.9rem'
                }}>
                  Offer Element Type
                </Typography>
                <Box sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1.5
                }}>
                  {customCategories.map(category => (
                    <Button
                      key={category}
                      variant={selectedCustomCategory === category ? 'contained' : 'outlined'}
                      color={selectedCustomCategory === category ? 'primary' : 'primary'}
                      size="medium"
                      sx={{
                        fontWeight: 500,
                        fontSize: '0.8rem',
                        minHeight: '32px',
                        py: 0.75,
                        px: 1.5,
                        whiteSpace: 'nowrap'
                      }}
                      onClick={() => {
                        setSelectedCustomCategory(category);
                        setSelectedCustomSubtype(null);
                        setSelectedVariantKey(null);
                        setSelectedTemplateId(null);
                      }}
                    >
                      {category}
                    </Button>
                  ))}
                </Box>
              </Box>

              {/* Row 2: Offer Element subtype */}
              <Box sx={{ pt: 1.5, pb: 1.5 }}>
                <Typography variant="subtitle2" sx={{
                  mb: 1.5,
                  color: 'primary.main',
                  fontWeight: 600,
                  textAlign: 'left',
                  fontSize: '0.9rem'
                }}>
                  Offer Element Subtype
                </Typography>
                <Box sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1.5
                }}>
                  {selectedCustomCategory ?
                    customElementsLibrary.filter(item => item.category === selectedCustomCategory).map(item => (
                      <Button
                        key={item.id}
                        variant={selectedCustomSubtype?.id === item.id ? 'contained' : 'outlined'}
                        color={selectedCustomSubtype?.id === item.id ? 'warning' : (item.label === 'Button w/text' ? 'warning' : 'primary')}
                        size="medium"
                        sx={{
                          fontWeight: 500,
                          fontSize: '0.8rem',
                          minHeight: '32px',
                          py: 0.75,
                          px: 1.5,
                          whiteSpace: 'nowrap'
                        }}
                        onClick={() => {
                          setSelectedCustomSubtype(item);
                          setSelectedVariantKey(null);
                          setSelectedTemplateId(null);
                        }}
                        disabled={item.elements.length === 0}
                      >
                        {item.label}
                      </Button>
                    )) :
                    <Typography variant="body2" sx={{
                      color: 'text.secondary',
                      fontStyle: 'italic',
                      py: 2
                    }}>
                      Select a type first
                    </Typography>
                  }
                </Box>
              </Box>

              {/* Row 3: Template Selection */}
              {selectedCustomSubtype && (
                <Box sx={{ borderTop: '1px solid #e0e0e0', pt: 1.5, pb: 1.5 }}>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 1,
                    mb: 1.5
                  }}>
                    <Typography variant="subtitle2" sx={{
                      color: 'primary.main',
                      fontWeight: 600,
                      textAlign: 'left',
                      fontSize: '0.9rem'
                    }}>
                      Select Template
                    </Typography>
                    {isBrandsPage && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<TemplateIcon />}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();

                          if (!selectedTemplateId) {
                            setShowLoadTemplateAlert(true);
                            setLoadTemplateError('Please select a template first from the library below.');
                            return;
                          }

                          try {
                            console.log('🔍 Insert Template Clicked');
                            let templateToLoad = null;

                            if (filteredTemplates && filteredTemplates.length > 0) {
                              templateToLoad = filteredTemplates.find(t => t._id === selectedTemplateId || t.id === selectedTemplateId);
                            }

                            if (!templateToLoad && savedOfferElements && savedOfferElements.length > 0) {
                              templateToLoad = savedOfferElements.find(t => t._id === selectedTemplateId || t.id === selectedTemplateId);
                            }

                            if (!templateToLoad && savedTemplates && savedTemplates.length > 0) {
                              templateToLoad = savedTemplates.find(t => t._id === selectedTemplateId || t.id === selectedTemplateId);
                            }

                            if (!templateToLoad) {
                              for (const subtype of customElementsLibrary) {
                                if (subtype.elements) {
                                  const found = subtype.elements.find(t => t._id === selectedTemplateId || t.id === selectedTemplateId);
                                  if (found) {
                                    templateToLoad = found;
                                    break;
                                  }
                                }
                              }
                            }

                            if (templateToLoad) {
                              loadPreBuiltTemplate(templateToLoad, true);
                            } else {
                              setShowLoadTemplateAlert(true);
                              setLoadTemplateError('Could not find the selected template data. Please try selecting it again.');
                            }
                          } catch (err) {
                            console.error('❌ Error in Insert Template:', err);
                            setShowLoadTemplateAlert(true);
                            setLoadTemplateError('An error occurred while inserting the template.');
                          }
                        }}
                        disabled={!selectedTemplateId}
                        color="secondary"
                        sx={{ py: 0.4, px: 1.2, fontSize: '0.75rem', textTransform: 'none' }}
                        title="Insert selected template into a new page in the editor below"
                      >
                        Insert Template
                      </Button>
                    )}
                  </Box>
                  {(filteredTemplates.length > 0) ? (
                    (() => {
                      // Get both user templates and prebuilt templates
                      const userTemplates = filteredTemplates.filter(t => !t.isPreBuilt);
                      const prebuiltTemplates = filteredTemplates.filter(t => t.isPreBuilt);

                      // Remove duplicates
                      const uniqueUserTemplates = userTemplates.filter((template, index, self) =>
                        index === self.findIndex(t => t._id === template._id)
                      );

                      const uniquePrebuiltTemplates = prebuiltTemplates.filter((template, index, self) =>
                        index === self.findIndex(t => t._id === template._id)
                      );

                      console.log('🎯 Template Selection Debug:', {
                        userTemplatesCount: uniqueUserTemplates.length,
                        prebuiltTemplatesCount: uniquePrebuiltTemplates.length,
                        userTemplates: uniqueUserTemplates.map(t => t.name),
                        prebuiltTemplates: uniquePrebuiltTemplates.map(t => t.name),
                        hasVariants: selectedCustomSubtype?.elements?.[0]?.variants ? Object.keys(selectedCustomSubtype.elements[0].variants).length : 0
                      });

                      return (
                        <Box>
                          {/* Show user templates */}
                          {uniqueUserTemplates.length > 0 && (
                            <Box sx={{ mb: 1.5 }}>

                              <Box sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 1.5
                              }}>
                                {uniqueUserTemplates.map(template => (
                                  <Button
                                    key={template._id}
                                    variant={selectedTemplateId === template._id ? 'contained' : 'outlined'}
                                    size="medium"
                                    sx={{
                                      fontWeight: 500,
                                      fontSize: '0.8rem',
                                      minHeight: '32px',
                                      py: 0.75,
                                      px: 1.5,
                                      whiteSpace: 'nowrap'
                                    }}
                                    onClick={() => {
                                      setSelectedTemplateId(template._id);
                                    }}
                                  >
                                    {template.name}
                                  </Button>
                                ))}
                              </Box>
                            </Box>
                          )}

                          {/* Show prebuilt templates */}
                          {uniquePrebuiltTemplates.length > 0 && (
                            <Box sx={{ mb: 1.5 }}>
                              <Typography variant="caption" sx={{
                                display: 'block',
                                color: 'primary.main',
                                fontWeight: 600,
                                mb: 0.75,
                                fontSize: '0.75rem'
                              }}>
                                🎨 Prebuilt Templates
                              </Typography>
                              <Box sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 1.5
                              }}>
                                {uniquePrebuiltTemplates.map(template => (
                                  <Button
                                    key={template._id}
                                    variant={selectedTemplateId === template._id ? 'contained' : 'outlined'}
                                    size="medium"
                                    sx={{
                                      fontWeight: 500,
                                      fontSize: '0.8rem',
                                      minHeight: '32px',
                                      py: 0.75,
                                      px: 1.5,
                                      whiteSpace: 'nowrap'
                                    }}
                                    onClick={() => {
                                      setSelectedTemplateId(template._id);
                                    }}
                                  >
                                    {template.name}
                                  </Button>
                                ))}
                              </Box>
                            </Box>
                          )}

                          {/* Show built-in variants if no templates exist */}
                          {uniqueUserTemplates.length === 0 && uniquePrebuiltTemplates.length === 0 && selectedCustomSubtype?.elements?.[0]?.variants && (
                            <Box>
                              <Typography variant="caption" sx={{
                                display: 'block',
                                color: 'primary.main',
                                fontWeight: 600,
                                mb: 0.75,
                                fontSize: '0.75rem'
                              }}>
                                ⚡ Built-in Variants
                              </Typography>
                              <Box sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 1.5
                              }}>
                                {Object.keys(selectedCustomSubtype.elements[0].variants).map(variantKey => (
                                  <Button
                                    key={variantKey}
                                    variant={selectedVariantKey === variantKey ? 'contained' : 'outlined'}
                                    size="medium"
                                    onClick={() => {
                                      setSelectedVariantKey(variantKey);
                                      const variantData = selectedCustomSubtype.elements[0].variants[variantKey];
                                      if (variantData) {
                                        addCustomElement({ elements: [variantData] });
                                      }
                                    }}
                                    sx={{
                                      fontWeight: 500,
                                      fontSize: '0.8rem',
                                      minHeight: '32px',
                                      py: 0.75,
                                      px: 1.5,
                                      whiteSpace: 'nowrap'
                                    }}
                                  >
                                    {variantKey.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                  </Button>
                                ))}
                              </Box>
                            </Box>
                          )}

                          {/* Show message when no templates or variants available */}
                          {uniqueUserTemplates.length === 0 && uniquePrebuiltTemplates.length === 0 && (!selectedCustomSubtype?.elements?.[0]?.variants || Object.keys(selectedCustomSubtype.elements[0].variants).length === 0) && (
                            <Typography variant="body2" sx={{
                              color: 'text.secondary',
                              textAlign: 'center',
                              fontStyle: 'italic',
                              py: 3
                            }}>
                              No templates available for {selectedCustomSubtype ? selectedCustomSubtype.label : selectedCustomCategory}
                            </Typography>
                          )}
                        </Box>
                      );
                    })()
                  ) : (
                    <Typography variant="body2" sx={{
                      color: 'text.secondary',
                      textAlign: 'center',
                      fontStyle: 'italic',
                      py: 3
                    }}>
                      {selectedCustomCategory
                        ? 'No templates available for this category'
                        : 'Select a category from the top section'
                      }
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )
          }
          <Dialog
            open={showLoadTemplateAlert}
            onClose={() => setShowLoadTemplateAlert(false)}
          >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WarningIcon color="warning" /> Template Interaction
            </DialogTitle>
            <DialogContent>
              <Typography variant="body1">
                {loadTemplateError || 'Please select a template first.'}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowLoadTemplateAlert(false)} variant="contained" color="primary">
                OK
              </Button>
            </DialogActions>
          </Dialog>
        </Box >
      );
    }




    // Default combined view (original layout)
    return (
      <Box>
        {/* Combined view with all sections */}
        {!hideCustomElements && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
            {/* Type, Subtype, and Templates in original layout */}
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
              Combined template selection view
            </Typography>
          </Box>
        )}
      </Box>
    );
  }


  // ── Context value ─────────────────────────────────────────────────────────
  const contextValue = {
    // Props
    templatesOnly, customElementsOnly, hideCustomElements, hideLoadTemplate,
    templateSelectionOnly, typeAndSubtypeOnly, templatesOnlyMode, isBrandsPage,
    // Theme
    isMobile,
    // All state (from useOfferBuilderState)
    localElements, setLocalElements,
    localSelectedElement, setLocalSelectedElement,
    localSelectedElementContext, setLocalSelectedElementContext,
    localPages, setLocalPages,
    pages, setPages,
    currentPageIndex, setCurrentPageIndex,
    nextPageId, setNextPageId,
    paginationEnabled, setPaginationEnabled,
    isLoadingExistingContent, setIsLoadingExistingContent,
    insertAfterElementId, setInsertAfterElementId,
    localRatingText, setLocalRatingText,
    getCurrentPage, currentPageElements,
    elements, setElements,
    selectedElement, setSelectedElement,
    selectedElementContext, setSelectedElementContext,
    isEditingMode,
    selectedSubElement, setSelectedSubElement,
    drawerOpen, setDrawerOpen,
    previewPanelOpen, setPreviewPanelOpen,
    settingsOpen, setSettingsOpen,
    settingsAnchorEl, setSettingsAnchorEl,
    pageSettingsOpen, setPageSettingsOpen,
    tabValue, setTabValue,
    popoverPosition, setPopoverPosition,
    isDragging, setIsDragging,
    dragOffset, setDragOffset,
    showLayoutSelector, setShowLayoutSelector,
    targetZone, setTargetZone,
    draggedElement, setDraggedElement,
    dragOverZone, setDragOverZone,
    editingElement, setEditingElement,
    editingField, setEditingField,
    tempValue, setTempValue,
    deletedElementsHistory, setDeletedElementsHistory,
    showUndoNotification, setShowUndoNotification,
    contentTabTempValue, setContentTabTempValue,
    buttonContentTabTempValue, setButtonContentTabTempValue,
    buttonUrlTabTempValue, setButtonUrlTabTempValue,
    listItemTextValues, setListItemTextValues,
    savedTemplates, setSavedTemplates,
    preBuiltTemplates, setPreBuiltTemplates,
    buttonImageCalloutTemplates, setButtonImageCalloutTemplates,
    buttonNoticeTemplates, setButtonNoticeTemplates,
    buttonTextTemplates, setButtonTextTemplates,
    templateName, setTemplateName,
    templateType, setTemplateType,
    templateSubtype, setTemplateSubtype,
    showSaveDialog, setShowSaveDialog,
    showTemplatesList, setShowTemplatesList,
    currentTemplateId, setCurrentTemplateId,
    pageSettings, setPageSettings,
    mobileDrawerOpen, setMobileDrawerOpen,
    rightPanelOpen, setRightPanelOpen,
    previewDevice, setPreviewDevice,
    forceRender, setForceRender,
    textEditorRef,
    selectedCustomCategory, setSelectedCustomCategory,
    selectedCustomSubtype, setSelectedCustomSubtype,
    filteredTemplates, setFilteredTemplates,
    selectedVariantKey, setSelectedVariantKey,
    selectedTemplateId, setSelectedTemplateId,
    iconSelectorOpen, setIconSelectorOpen,
    imageUploading, setImageUploading,
    uploadingElementId, setUploadingElementId,
    generatingLink, setGeneratingLink,
    savedOfferElements, setSavedOfferElements,
    filteredSavedElements, setFilteredSavedElements,
    currentElementName, setCurrentElementName,
    showSaveElementDialog, setShowSaveElementDialog,
    showBrandOfferAlert, setShowBrandOfferAlert,
    showLoadTemplateAlert, setShowLoadTemplateAlert,
    loadTemplateError, setLoadTemplateError,
    showStatusAlert, setShowStatusAlert,
    statusMessage, setStatusMessage,
    statusTitle, setStatusTitle,
    showReplaceConfirm, setShowReplaceConfirm,
    showConfirmDialog, setShowConfirmDialog,
    confirmDialogTitle, setConfirmDialogTitle,
    confirmDialogMessage, setConfirmDialogMessage,
    confirmAction, setConfirmAction,
    showPromptDialog, setShowPromptDialog,
    promptDialogTitle, setPromptDialogTitle,
    promptDialogMessage, setPromptDialogMessage,
    promptDialogValue, setPromptDialogValue,
    promptAction, setPromptAction,
    pendingTemplateData, setPendingTemplateData,
    showRenameDialog, setShowRenameDialog,
    renameElementId, setRenameElementId,
    renameElementValue, setRenameElementValue,
    isUpdatingElement, setIsUpdatingElement,
    selectedSavedElementId, setSelectedSavedElementId,
    saveType, setSaveType,
    selectedBrand, setSelectedBrand,
    selectedOffer, setSelectedOffer,
    brands, setBrands,
    offers, setOffers,
    // Zone height (from useZoneHeight)
    getZoneHeight, getZoneHeightStyle, checkZoneHeightOverflow, clearOverflowAlert,
    getOverflowMessage, getCanvasOverflowAlert, toggleZoneHeight,
    getExpandIcon, getHeightTooltip, getHeightModeText, STANDARD_MOBILE,
    // Pagination (from usePagination)
    calculateElementHeight, calculatePageHeight, checkBrandsPageOverflow,
    getBrandsPageOverflowInfo, getMaxPageHeight, checkPageOverflow,
    checkAndEnablePagination, togglePagination, splitPageAtElement,
    mergeWithNextPage, moveToNewPage, addPage, goToPage, goToNextPage,
    goToPreviousPage, deletePage, addElement, MOBILE_PHONE_HEIGHT,
    // Element operations (from useElementOperations)
    findAndUpdateLayout, addLayout, addElementToZone, deleteElementFromZone,
    handleDragStart, handleDragOver, handleDragLeave, handleDrop, onDragEnd,
    startInlineEdit, saveInlineEdit, cancelInlineEdit, handleKeyPress,
    handleMouseDown, handleMouseMove, handleMouseUp,
    handleResizeMove, handleResizeEnd, handleResizeStart,
    deleteElement, undoDelete, dismissUndoNotification, updateElement,
    saveContentTabEdit, cancelContentTabEdit, handleContentTabKeyPress,
    saveButtonContentTabEdit, cancelButtonContentTabEdit, handleButtonContentTabKeyPress,
    saveButtonUrlTabEdit, cancelButtonUrlTabEdit, handleButtonUrlTabKeyPress,
    moveElement,
    // Templates (from useTemplates)
    loadTemplates, saveTemplate, loadTemplate, deleteTemplate, createNewTemplate,
    loadPreBuiltTemplate, loadPreBuiltTemplates, loadAllTemplatesForPopup,
    loadTemplatesByTypeAndSubtype, loadTemplatesByType, forceTemplateStateReset,
    filterLegitimateTemplates, groupTemplatesByType,
    // Saved elements (from useSavedElements)
    saveCurrentElement, loadSavedElement, deleteSavedElement,
    renameSavedElement, editSavedElement, loadSavedOfferElements,
    // Brands (from useBrands)
    populateOfferLinkInElements, loadBrands, loadOffersForBrand, handleBrandChange,
    handleVideoUpload, handleThumbnailUpload, handleImageUpload, handleMediaUploadForElement,
    // Local helpers defined in orchestrator
    generateId, generateHTML, handleGenerateLink,
    getPreviewStyles, getCanvasStyles,
    addCustomElement,
    elementLibrary, customCategories, customElementsLibrary,
    // Element renderer
    renderInlineEditableElement,
    // applyHighlight / removeAllHighlights (bound with setTempValue)
    applyHighlight: (color, editorRef) => applyHighlight(color, editorRef, setTempValue),
    removeAllHighlights: (editorRef) => removeAllHighlights(editorRef, setTempValue),
    // Color / font helpers (passed through for RightPanel tabs)
    getInlineBackgroundColors,
    getInlineTextColors,
    getCurrentFontFamily,
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <OfferBuilderContext.Provider value={contextValue}>
      <Box key={`offer-builder-${forceRender}`} sx={{ position: 'relative' }}>
        <Box sx={{ display: 'flex', height: '100%', position: 'relative' }}>
          <LeftPanel>
            <PreviewCanvas />
          </LeftPanel>
          <RightPanel />
        </Box>
        <MobileToolbar />
      </Box>
    </OfferBuilderContext.Provider>
  );
});

export default OfferPageBuilderSimple;
